// /Users/donovan68/dva-frontend-vite/src/pages/pro/PortfolioExplorationPage.tsx
// Step 1: Portfolio Exploration - Pro workspace with strategic alignment matrix and prospect list
// UPDATED: New layout - Header above journey bar, breadcrumbs removed
// FIX: Added missing buildApiUrl import

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Database } from "lucide-react";
import { ProProgressPathV3 } from "../../components/ProProgressPathV3";
import ProJourneyPageHeader from "../../components/journey/pro/ProJourneyPageHeader";
import ProspectPanel, { type Prospect } from "../../components/ProspectPanel";
import { useProspectList } from "../../hooks/useProspectList";
import { convertToProspectPanelFormat } from "../../utils/prospectUtils";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumCard } from "../../components/FreemiumCard";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import { useClient } from "../../contexts/ClientContext";
import { buildApiUrl } from "../../config/appConfig";

// Import MatrixPage for Pro Tools view
import MatrixPage from "../MatrixPage.jsx";


export default function PortfolioExplorationPage() {
  const navigate = useNavigate();
  const { activeFilter, setActiveFilter } = useFilter();
  const { setPageContext } = usePageContext();
  const { clientId } = useClient();
  const { prospects: rawProspects, updateStatus, refetch: refetchProspects } = useProspectList(clientId || '');

  const [error, setError] = useState<string | null>(null);

  const prospects = convertToProspectPanelFormat(rawProspects);

  // Handlers for ProspectPanel
  const handleProspectRemove = async (prospectId: string) => {
    if (!clientId) return;
    try {
      await updateStatus(Number(prospectId), 'qualified');
    } catch (err) {
      console.error('Failed to remove prospect:', err);
    }
  };

  const handleProfileClick = (prospectId: string) => {
    navigate(`/usecase/${prospectId}`);
  };

  const handleRoiClick = (prospectId: string) => {
    navigate(`/pro/analysis/${prospectId}`);
  };

  const handlePlanClick = (prospectId: string) => {
    navigate(`/pro/roadmap-planning`);
  };

  const handleReorder = (reordered: Prospect[]) => {
    // TODO: Implement reordering API call if needed
    console.log('Prospects reordered:', reordered);
  };

  // TODO: Replace with real data from hooks/services
  const personalizationData = {
    strategy: 75,
    capabilities: 80,
    readiness: 70,
    docsCount: 5
  };

  // Set page context - REMOVED breadcrumbs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [],
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  console.log('🏢 PortfolioExplorationPage - activeFilter:', activeFilter);
  console.log('🏢 Current clientId:', clientId);

  // Handle phase navigation (updated for 3-step system)
  const handlePhaseClick = (phaseId: string) => {
    switch(phaseId) {
      case 'portfolio':
        navigate("/home");
        break;
      case 'analysis':
        navigate("/pro/analysis");
        break;
      case 'approval_execution':
        navigate("/pro/approval");
        break;
      default:
        navigate("/home");
    }
  };

  // Handle adding use case to prospect list
  const handleAddToProspect = async (useCaseId: number) => {
    if (!clientId) {
      console.error('Cannot add to prospect: no clientId');
      return;
    }

    try {
      console.log(`➕ [PortfolioExplorationPage] Adding use case ${useCaseId} to prospect list for client ${clientId}...`);

      const response = await fetch(
        buildApiUrl(`/api/v2/usecases/${clientId}/${useCaseId}/status`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'prospect' }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [PortfolioExplorationPage] API error ${response.status}:`, errorText);
        throw new Error(`Failed to add to prospect list: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [PortfolioExplorationPage] Added to prospect list:', data);
      console.log('✅ [PortfolioExplorationPage] Response data:', JSON.stringify(data, null, 2));

      // Refresh the prospect list to get updated data
      console.log('🔄 [PortfolioExplorationPage] Refreshing prospect list...');
      await refetchProspects();
      console.log('✅ [PortfolioExplorationPage] Prospect list refreshed');

    } catch (error) {
      console.error('❌ [PortfolioExplorationPage] Error adding to prospect list:', error);
      setError(error instanceof Error ? error.message : 'Failed to add to prospect list');
    }
  };



  // DOCUMENTS CONTENT
  const documentsCards = [
    {
      icon: <Database className="w-6 h-6" />,
      title: "Executive Dashboard Guide",
      description: "Comprehensive guide to interpreting your KPIs and portfolio metrics.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Understanding ROI potential calculations",
        "Readiness index methodology",
        "Execution momentum tracking",
        "Portfolio optimization strategies"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "4.2 MB"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Pro Feature Overview",
      description: "Complete guide to Pro workspace capabilities and advanced analytics.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Custom ROI modeling tools",
        "Advanced reporting features",
        "Collaboration capabilities",
        "Integration options"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "6.1 MB"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Best Practices Guide",
      description: "Proven strategies for maximizing value from your DVA Pro workspace.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Data quality optimization",
        "Stakeholder engagement",
        "Portfolio prioritization",
        "Change management"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "3.8 MB"
    },
  ];

  const messageCount = 4;

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      
      {/* NEW LAYOUT ORDER: Tabs → Page Header → Journey Bar → Content */}
      
      {/* 0. Content Tabs (Tools / Documents / Messages) - Same position as header tabs */}
      <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
        <nav className="flex items-center gap-2" aria-label="Section navigation">
          {['Tools', 'Documents', 'Messages'].map((tab) => {
            const active = tab === activeFilter;
            const isMessages = tab === 'Messages';
            const showBadge = isMessages && messageCount > 0;
            
            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      
      {/* 1. Page Header */}
      <div className="mb-6 -mt-2">
        <ProJourneyPageHeader
          stepNumber={1}
          stepName="Portfolio Exploration"
          description="Visualize and prioritize initiatives by strategic value and implementation feasibility. Click any point for details or add to your Prospect List."
        />
      </div>

      {/* 2. Journey Bar */}
      <div className="mb-6">
        <ProProgressPathV3 
          currentPhase="portfolio"
          personalizationData={personalizationData}
          onPersonalizationClick={() => navigate("/personalize")}
          onPhaseClick={handlePhaseClick}
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">⚠️ {error}</p>
        </div>
      )}

      {/* 3. Main content with fixed right panel */}
      <div className="flex gap-0">
        {/* LEFT: main page content */}
        <div className="flex-1 min-w-0">
          {activeFilter === 'Messages' ? (
            <MessagesContent />
          ) : activeFilter === 'Documents' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documentsCards.map((card, idx) => (
                <FreemiumCard
                  key={idx}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  contentType={card.contentType}
                  listItems={card.listItems}
                  isPremium={card.isPremium}
                />
              ))}
            </div>
          ) : (
            <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-0 overflow-hidden">
              <MatrixPage 
                onUseCaseClick={handleAddToProspect}
              />
            </section>
          )}
        </div>

        {/* RIGHT: Prospect Panel - Fixed */}
        {clientId && (
          <div className="hidden lg:block">
            <ProspectPanel
              prospects={prospects}
              onRemove={handleProspectRemove}
              onProfileClick={handleProfileClick}
              onRoiClick={handleRoiClick}
              onPlanClick={handlePlanClick}
              onReorder={handleReorder}
            />
          </div>
        )}
      </div>
    </main>
  );
}